
// region JS Imports

// region External Modules
import { EventEmitter } from "events"

import Promise from "plugins/bluebird/index"
import u from "umbrellajs"
// endregion External Modules

// Private Modules
import Google_Event from "app/tracking/google/event"
import PageInstance from "app/page_instance/index"
import Modal from "app/modal/show-modal"
import Auth_action from "app/authentication/action"
import Auth_callback_queue from "app/authentication/callback_queue"

import {
  refreshAndUpdateComponentPromise,
} from "app/authentication/remote_state"

import {
  captureMessage,
  captureException,
} from "app/error_reporting"

import {
  getContactInfoData,
  setContactInfoData,
} from "app/local_storages/contact_info_storage"

import {
  create as create_user,
  ResultTypeEnums as CreateUserResultTypes,
} from "app/resources/users/create/index"

import {
  DataEntry as NeighbourhoodFollowingDataEntry,
} from "app/resources/current_authenticated_user/neighbourhood_followings/get_many/index"

import EventStore from "app/resources/neighbourhood_followings/event_store"

import {
  isPresentString,
} from "app/utils/type_checking"

import {
  isSignedIn,
} from "app/authentication/state"

import {
  subscribe as subscribe_neighbourhood_following,
  ResultTypeEnums as SubscribeNeighbourhoodFollowingResultTypes,
  FollowTypes as SubscribeNeighbourhoodFollowingFollowTypes,
} from "app/resources/neighbourhood_followings/subscribe/index"

import {
  unsubscribe as unsubscribe_neighbourhood_following,
  ResultTypeEnums as UnsubscribeNeighbourhoodFollowingResultTypes,
  FollowTypes as UnsubscribeNeighbourhoodFollowingFollowTypes,
} from "app/resources/neighbourhood_followings/unsubscribe/index"

import {
  trackSignUp,
} from "app/authentication/tracking"

import {
  getLastContactInfoSyncPromise,
} from "app/authentication/contact_info_caching"
import GA4_Event from "app/tracking/google/google_analytics_4/event"
// Private Modules

// region Public Components
// endregion Public Components

// region Private Components
// endregion Private Components

// endregion JS Imports


const RejectionReasons = {
  ERROR_REPORTED: "ERROR_REPORTED",
}

const SOURCE_FILE_PATH = "concepts/neighbourhoods/neighbourhood_follow_button/factory"


interface Inputs {
  container: HTMLElement,

  neighbourhood_followings?: NeighbourhoodFollowingDataEntry[],

  followButtonPrimary?: string,
  unfollowButtonPrimary?: string,
}

interface Selector {
  container: HTMLElement,

  neighbourhood_followings: NeighbourhoodFollowingDataEntry[],

  followButtonPrimary: string,
  unfollowButtonPrimary: string,
}

const DEFAULT_SELECTOR = {
  neighbourhood_followings: [],

  followButtonPrimary:   ".follow-button-content.on-not-following .cell--neighbourhoods--following--button__link",
  unfollowButtonPrimary: ".follow-button-content.on-following .cell--neighbourhoods--following--button__link",
}

export class NeighbourhoodButton extends EventEmitter {
  private selector: Selector

  private container_el: HTMLElement
  private $container: UmbrellaInstance
  private readonly neighbourhood_id: string
  private readonly follow_type: string

  private readonly $followButtonPrimary = null as UmbrellaInstance | null
  private readonly $unfollowButtonPrimary = null as UmbrellaInstance | null

  constructor(selector: Inputs) {
    super()

    this.selector = Object.assign({}, DEFAULT_SELECTOR, selector)

    this.container_el = this.selector.container
    this.$container = u(this.selector.container)
    this.neighbourhood_id = this.$container.data("neighbourhood-id").toString()
    this.follow_type = this.$container.data("follow-type")

    if ( this.$container.length === 1 ) {
      this.$followButtonPrimary = this.$container.find(this.selector.followButtonPrimary)
      this.$unfollowButtonPrimary = this.$container.find(this.selector.unfollowButtonPrimary)

      this.bind_action()
      this.created()
    }
    else {
      captureMessage(
        `$container contains non 1 (${this.$container.length}) elements`,
        {
          source_file_path: SOURCE_FILE_PATH,
        }
      )
    }
  }

  private created() {
    const matched_followings_found =
      this.selector.neighbourhood_followings.length > 0 &&
      this.selector.neighbourhood_followings.some((neighbourhood_following) => {
        return neighbourhood_following.neighbourhood_id === this.neighbourhood_id && (
          (this.follow_type === "commercial" && neighbourhood_following.is_following_commercial_updates) ||
          (this.follow_type !== "commercial" && neighbourhood_following.is_following_residential_updates)
        )
      })
    if (matched_followings_found) {
      this.displayAsFollowing()
    }
  }

  private bind_action(){
    if (this.$followButtonPrimary == null) { return }
    if (this.$unfollowButtonPrimary == null) { return }

    const $followButtonPrimary = this.$followButtonPrimary
    const $unfollowButtonPrimary = this.$unfollowButtonPrimary

    $followButtonPrimary
    .on("click", (e) => {
      e.preventDefault()

      const follow_type: string = this.$container.data("follow-type")

      const btn_el = $followButtonPrimary.first() as HTMLButtonElement | false
      try {
        if (btn_el) {
          btn_el.disabled = true
        }
        this.onFollowClick(follow_type)
      }
      finally {
        if (btn_el) {
          btn_el.disabled = false
        }
      }
    })

    $unfollowButtonPrimary
    .on("click", (e) => {
      e.preventDefault()

      const btn_el = $unfollowButtonPrimary.first() as HTMLButtonElement | false
      try {
        if (btn_el) {
          btn_el.disabled = true
        }
        this.onUnfollowClick(this.follow_type)
      }
      finally {
        if (btn_el) {
          btn_el.disabled = false
        }
      }
    })

    EventStore.onSubscribe(({neighbourhood_id, follow_type}) => {
      if (neighbourhood_id.toString() !== this.neighbourhood_id) { return }
      if (follow_type !== this.follow_type) { return }

      this.displayAsFollowing()
    })
    EventStore.onUnsubscribe(({neighbourhood_id, follow_type}) => {
      if (neighbourhood_id.toString() !== this.neighbourhood_id) { return }
      if (follow_type !== this.follow_type) { return }

      this.displayAsNotFollowing()
    })

    return this
  }

  private onFollowClick(follow_type: string) {
    Modal.showLoadingIndicator()

    const local_contact_info_data = getContactInfoData()

    if (! isPresentString(local_contact_info_data.contact_email_address)) {
      Auth_callback_queue.add(() => {
        getLastContactInfoSyncPromise()
        .then(() => this.onFollowClick(follow_type))
        .done()
      }, "event:create_neighbourhood_following")

      Auth_action.showSignUpModal({
        enable_account_already_exists_error_message: false,
      })
      .then(({$modal, authentication_box}) => {
        authentication_box.on("sign_up:fail:account_conflict", ({email_address}) => {
          $modal.modal("hide")
          // Retry AFTER setting local contact email
          setContactInfoData({
            contact_email_address: email_address,
          })
          this.onFollowClick(follow_type)
        })
      })
      .done()
      return
    }

    let subscribe_neighbourhood_following_inputs = {}
    Promise.resolve()
    .then(() => {
      Modal.showLoadingIndicator()

      if (isSignedIn()) {
        return Promise.resolve()
      }

      return create_user({
        authentication_handle:  local_contact_info_data.user_authentication_handle || local_contact_info_data.contact_email_address || "",
        email_address:          local_contact_info_data.contact_email_address || "",
        sign_in_on_success:     true,
      })
      .then(({result_type}) => {
        switch (result_type) {
          case CreateUserResultTypes.SUCCESS:
            trackSignUp()
            refreshAndUpdateComponentPromise().done()
            return
          case CreateUserResultTypes.CONFLICT:
            return
          default:
            captureMessage(
              `unexpected/unhandled result_type: <${result_type}>`,
              {
                source_file_path:
                  "app/assets/javascripts/concepts/listings/contact/button/events/index",
              }
            )
            throw RejectionReasons.ERROR_REPORTED
        }
      })
    })
    .then(() => {
      const inputs = {
        // Need `||` to make flow happy
        user_authentication_handle: local_contact_info_data.user_authentication_handle || local_contact_info_data.contact_email_address || "",

        neighbourhood_id: this.neighbourhood_id,
        follow_type:      follow_type === "commercial" ?
          SubscribeNeighbourhoodFollowingFollowTypes.COMMERCIAL :
          SubscribeNeighbourhoodFollowingFollowTypes.RESIDENTIAL,
      }
      subscribe_neighbourhood_following_inputs = inputs
      return subscribe_neighbourhood_following(inputs)
    })
    .then(({result_type}) => {
      switch (result_type) {
        case SubscribeNeighbourhoodFollowingResultTypes.SUCCESS:
          return
        default:
          captureMessage(
            `unexpected/unhandled result_type from create_neighbourhood_following: <${result_type}>`,
            Object.assign({
              "source_file_path":
                SOURCE_FILE_PATH,
              "local_contact_info_data.contact_email_address":
                local_contact_info_data.contact_email_address || "unknown",
            }, subscribe_neighbourhood_following_inputs),
          )
          throw RejectionReasons.ERROR_REPORTED
      }
    })
    .then(() => {
      Modal.hideLoadingIndicator()
      EventStore.fireSubscribeEvent({
        neighbourhood_id: this.neighbourhood_id,
        follow_type:      follow_type,
      })

      // # Tracking
      Google_Event.track({
        category: PageInstance.currentPageNameForTracking(),
        action:   "Follow",
        options:  {
          eventLabel: "Follow Neighbourhood - Followed",
        },
      })

      GA4_Event.track({
        event_name: "neighbourhood_followed",
        options:    {
          page: PageInstance.currentPageNameForTracking(),
        },
      })
    })
    .catch((whatever) => {
      Modal.hideLoadingIndicator()
      if (whatever === RejectionReasons.ERROR_REPORTED) { return }

      if (whatever instanceof Error) {
        captureException(
          whatever,
          {
            source_file_path:
            SOURCE_FILE_PATH,
          }
        )
        return
      }

      captureMessage(
        whatever.toString(),
        {
          source_file_path:
          SOURCE_FILE_PATH,
        }
      )
    })
    .done()
  }

  private onUnfollowClick(follow_type: string) {
    const local_contact_info_data = getContactInfoData()

    if (! isPresentString(local_contact_info_data.contact_email_address)) {
      // Do nothing but just pretend to have unfollow
      EventStore.fireUnsubscribeEvent({
        neighbourhood_id: this.neighbourhood_id,
        follow_type:      follow_type,
      })
      return
    }

    Promise.resolve()
    .then(() => {
      return unsubscribe_neighbourhood_following({
        // Need `||` to make flow happy
        user_authentication_handle: local_contact_info_data.user_authentication_handle || local_contact_info_data.contact_email_address || "",

        neighbourhood_id: this.neighbourhood_id,
        follow_type:      follow_type === "commercial" ?
          UnsubscribeNeighbourhoodFollowingFollowTypes.COMMERCIAL :
          UnsubscribeNeighbourhoodFollowingFollowTypes.RESIDENTIAL,
      })
    })
    .then(({result_type}) => {
      switch (result_type) {
        case UnsubscribeNeighbourhoodFollowingResultTypes.SUCCESS:
          return
        default:
          captureMessage(
            `unexpected/unhandled result_type from destroy_neighbourhood_following: <${result_type}>`,
            {
              source_file_path:
              SOURCE_FILE_PATH,
            }
          )
          throw RejectionReasons.ERROR_REPORTED
      }
    })
    .then(() => {
      EventStore.fireUnsubscribeEvent({
        neighbourhood_id: this.neighbourhood_id,
        follow_type:      follow_type,
      })
    })
    .catch((whatever) => {
      if (whatever === RejectionReasons.ERROR_REPORTED) { return }

      if (whatever instanceof Error) {
        captureException(
          whatever,
          {
            source_file_path:
            SOURCE_FILE_PATH,
          }
        )
        return
      }

      captureMessage(
        whatever.toString(),
        {
          source_file_path:
          SOURCE_FILE_PATH,
        }
      )
    })
    .done()
  }

  private displayAsFollowing() {
    this.container_el.classList.add("following")

    this.emit("neighbourhood_following")
  }
  private displayAsNotFollowing() {
    this.container_el.classList.remove("following")
  }
}

const Factory = {
  _instance: null as null | NeighbourhoodButton,
  instance(selector: Inputs){
    if (this._instance !== null) { return this._instance }

    this._instance = this.create(selector)
    return this._instance
  },
  create(selector: Inputs){
    return new NeighbourhoodButton(selector)
  },
}

export {
  Factory,
}
